import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Editors open from the row they belong to (26/10/26, Jamie: no form appearing at the top of the page, away from what
 * you tapped). Two shapes, one behaviour:
 * - `RowSheet`: a Sheet (@tc/ui) opened from a row, or from "Add" with the same form empty. It fills the screen on a
 *   phone and is a centred panel on a bigger screen.
 * - `RowEdit`: the same, inline right under the row, for a small edit (two or three boxes).
 * Both: focus goes in when they open and back to the row's button when they close; Escape closes; closing with changes
 * not saved asks first (inside the editor, not a browser box); an error shows inside, next to Save. What a page saves,
 * checks and allows is unchanged: this only decides where the form is drawn.
 *
 * Mount it while editing (`{edit && <RowSheet …>}`). It compares `value` with what it held when it opened to know
 * whether anything changed (or pass `dirty`). Inside, `<CloseButton>` is a Cancel that asks the same question.
 * Shared in @tc/ui (26/10/26, Modules' request): it replaced seven identical per-module copies. The styles are in ui.css
 * (.tc-rs-*).
 */
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { Sheet } from "./sheet.js";
const snap = (v) => { try {
    return JSON.stringify(v) ?? "";
}
catch {
    return "";
} };
const FIELDS = "input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled])";
/**
 * Where focus goes back to when the editor closes: the button that opened it, or, when the list has been drawn again
 * since (a save reloads it), the same row by its `id` or `data-row`. Left alone when something else (another sheet)
 * has taken focus meanwhile.
 */
export function rememberOpener() {
    if (typeof document === "undefined")
        return () => undefined;
    const a = document.activeElement;
    const opener = a instanceof HTMLElement && a !== document.body ? a : null;
    const id = opener?.id || null;
    const row = opener?.closest("[data-row]")?.dataset.row ?? null;
    return () => {
        window.setTimeout(() => {
            const now = document.activeElement;
            if (now && now !== document.body && now.isConnected)
                return;
            let t = opener?.isConnected ? opener : null;
            if (!t && id)
                t = document.getElementById(id);
            if (!t && row) {
                // The one on screen (a page can draw the same row twice: a grid on a desk, a list on a phone).
                const host = [...document.querySelectorAll(`[data-row="${CSS.escape(row)}"]`)].find((x) => x.getClientRects().length > 0);
                t = host?.matches("button, a, [tabindex]") ? host : host?.querySelector("button, a, [tabindex]") ?? null;
            }
            t?.focus();
        }, 300);
    };
}
const Close = createContext(() => undefined);
/** The editor's own close, which asks first when something has changed. */
export const useRowClose = () => useContext(Close);
/** A Cancel (or Close) button for inside a RowSheet / RowEdit: asks first when something has changed. */
export function CloseButton({ children = "Cancel" }) {
    const close = useRowClose();
    return _jsx("button", { type: "button", className: "tc-btn", onClick: close, children: children });
}
function useGuard(value, dirty, onClose) {
    const [start] = useState(() => snap(value));
    const [back] = useState(rememberOpener);
    useEffect(() => back, [back]);
    const changed = dirty ?? (value !== undefined && snap(value) !== start);
    const [asked, setAsked] = useState(0);
    const box = useRef(null);
    const stay = useRef(null);
    // The question in view (not under a sheet's sticky buttons) with focus on "Keep editing".
    useEffect(() => { if (asked) {
        stay.current?.focus({ preventScroll: true });
        stay.current?.scrollIntoView({ block: "center" });
    } }, [asked]);
    const close = () => { if (changed)
        setAsked((n) => n + 1);
    else
        onClose(); };
    const keep = () => { setAsked(0); box.current?.querySelector(FIELDS)?.focus(); };
    const ask = asked > 0 && changed ? (_jsxs("div", { className: "tc-rs-ask", role: "alertdialog", "aria-label": "Changes not saved", children: [_jsx("span", { children: "Your changes aren't saved." }), _jsxs("span", { className: "tc-rs-ask-b", children: [_jsx("button", { ref: stay, type: "button", className: "tc-btn", onClick: keep, children: "Keep editing" }), _jsx("button", { type: "button", className: "tc-btn tc-rs-drop", onClick: onClose, children: "Close without saving" })] })] })) : null;
    return { close, ask, box };
}
const Err = ({ error }) => (error ? _jsx("div", { className: "tc-error tc-rs-err", role: "alert", children: error }) : null);
/** An editor in a sheet, opened from its row (or empty from Add). */
export function RowSheet({ title, onClose, value, dirty, error, foot, wide, children }) {
    const g = useGuard(value, dirty, onClose);
    return (_jsx(Close.Provider, { value: g.close, children: _jsx(Sheet, { open: true, guard: false, onClose: g.close, title: title, wide: wide, foot: g.ask || foot ? _jsxs("div", { className: "tc-rs-foot", children: [g.ask, foot] }) : undefined, children: _jsxs("div", { ref: g.box, className: "tc-rs", children: [children, _jsx(Err, { error: error })] }) }) }));
}
/** A small editor inline, right under its row. */
export function RowEdit({ title, onClose, value, dirty, error, foot, children }) {
    const g = useGuard(value, dirty, onClose);
    useEffect(() => {
        const el = g.box.current;
        el?.scrollIntoView({ block: "nearest" });
        (el?.querySelector(FIELDS) ?? el)?.focus({ preventScroll: true });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    // Escape closes this editor only, not a sheet it sits in.
    const key = (e) => { if (e.key === "Escape") {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        g.close();
    } };
    return (_jsx(Close.Provider, { value: g.close, children: _jsxs("div", { ref: g.box, className: "tc-rs-inline", role: "group", "aria-label": title, tabIndex: -1, onKeyDown: key, children: [title && _jsx("b", { className: "tc-rs-title", children: title }), children, _jsx(Err, { error: error }), g.ask, foot && _jsx("div", { className: "tc-rs-actions", children: foot })] }) }));
}
